import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import { ReplyToItem } from '../../../contexts/ReplyToContext';
import { ReactComponent as CloseIcon } from '../../../icons/close.svg';
import { ReactComponent as CardAgentIcon } from '../../../icons/card-agent.svg';
import { ReactComponent as CardVisaIcon } from '../../../icons/card-visa.svg';
import { ReactComponent as CardAmexIcon } from '../../../icons/card-amex.svg';
import { ReactComponent as CardMastercardIcon } from '../../../icons/card-mastercard.svg';
import { ReactComponent as CardOtherIcon } from '../../../icons/card-other.svg';
import { ReactComponent as CardAddIcon } from '../../../icons/card-add.svg';
import AddPaymentCardModal, {
  AddPaymentCardResult,
} from './AddPaymentCardModal';
import SpendingOnboardingModal from './SpendingOnboardingModal';
import { useOnboardingModals } from '../../../providers';
import api from '../../../api';
import './response-artifact-modal.scss';
import './spending-approval-modal.scss';

interface TopUpOption {
  priceId: string;
  unitAmount: number;
  unitsPerProduct: number;
  quantity: number;
  totalCost: number;
  totalCredits: number;
  currency: string;
}

interface SpendReadiness {
  sufficient: boolean;
  balance: number;
  shortfall: number;
  topUpOption?: TopUpOption | null;
}

interface DepositReadiness {
  ready: boolean;
  reason?: 'negative_purchase';
  purchaseBalance: number;
  shortfall?: number;
  topUpOption?: TopUpOption | null;
}

type ModalPhase =
  | 'checking'
  | 'ready'
  | 'topup-required'
  | 'topping-up'
  | 'error';

export interface CardSelection {
  cardType: 'agent' | 'user_card' | 'merchant_stored';
  credentialId?: string;
  cardHint?: string;
}

interface SavedCard {
  id: string;
  label: string;
  cardLastFour: string | null;
  cardNetwork: string | null;
  createdAt: string;
}

const AGENT_CARD_ONLY_ACTIONS = new Set(['buy_and_act', 'bring_and_buy']);

interface SpendingApprovalModalProps {
  show: boolean;
  onApprove: (cardSelection?: CardSelection) => void;
  onCancel: () => void;
  onReject?: () => void;
  items: ReplyToItem[];
  inline?: boolean;
  agentImageUrl?: string;
  assistantName?: string;
  isDeposit?: boolean;
  context?: string;
  actionKey?: string;
  merchantName?: string;
}

function getCardIcon(network: string | null) {
  switch (network) {
    case 'visa':
      return <CardVisaIcon />;
    case 'mastercard':
      return <CardMastercardIcon />;
    case 'amex':
      return <CardAmexIcon />;
    default:
      return <CardOtherIcon />;
  }
}

const SpendingApprovalModal: React.FC<SpendingApprovalModalProps> = ({
  show,
  onApprove,
  onCancel,
  onReject,
  items,
  inline,
  agentImageUrl,
  assistantName = 'Assistant',
  isDeposit,
  context,
  actionKey,
  merchantName,
}) => {
  const cardLabel = `${assistantName}\u2019s Card`;
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: authLoading,
    loginWithRedirect,
  } = useAuth0();
  const { hasSeenModal, loading: onboardingLoading } = useOnboardingModals();

  // The spending approval / mandate flow requires authentication (saved cards,
  // readiness checks, marking onboarding as seen). On the public shared-artifact
  // page a logged-out user would otherwise get stuck in an onboarding loop, so
  // send them to log in and return to this page.
  const needsLogin = show && !authLoading && !isAuthenticated;
  useEffect(() => {
    if (needsLogin) {
      loginWithRedirect({
        appState: {
          path: window.location.pathname + window.location.search,
        },
      });
    }
  }, [needsLogin, loginWithRedirect]);

  const [phase, setPhase] = useState<ModalPhase>('checking');
  const [readiness, setReadiness] = useState<SpendReadiness | null>(null);
  const [topUpOption, setTopUpOption] = useState<TopUpOption | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [depositReadiness, setDepositReadiness] =
    useState<DepositReadiness | null>(null);

  // Card chooser state
  const [selectedCard, setSelectedCard] = useState<'agent' | string>(
    isDeposit ? '' : 'agent',
  );
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [cardHint, setCardHint] = useState('');

  // Accordion state: which section is expanded
  const [activeSection, setActiveSection] = useState<
    'savedCards' | 'merchantStored'
  >('savedCards');

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);

  const bookableItems = useMemo(
    () => items.filter((item) => item.bookable),
    [items],
  );

  const totalsByCurrency = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const item of bookableItems) {
      if (item.price != null && item.price_currency) {
        totals[item.price_currency] =
          (totals[item.price_currency] || 0) + item.price;
      }
    }
    return totals;
  }, [bookableItems]);

  const totalAmount = useMemo(() => {
    return Object.values(totalsByCurrency).reduce((sum, v) => sum + v, 0);
  }, [totalsByCurrency]);

  const formatPrice = (amount: number, currency: string) => {
    try {
      const isWholeNumber = amount % 1 === 0;
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: isWholeNumber ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${amount} ${currency}`;
    }
  };

  const totalEntries = Object.entries(totalsByCurrency);
  const totalDisplay = totalEntries
    .map(([cur, amt]) => formatPrice(amt, cur))
    .join(' + ');

  const agentCardOnly =
    !isDeposit && !!actionKey && AGENT_CARD_ONLY_ACTIONS.has(actionKey);

  const isMerchantStoredSelected = activeSection === 'merchantStored';
  const isAgentCardSelected =
    !isMerchantStoredSelected && selectedCard === 'agent';
  const isUserCardSelected =
    !isMerchantStoredSelected &&
    selectedCard !== 'agent' &&
    selectedCard !== '';

  // ── Fetch saved payment cards ────────────────────────────────────
  const fetchSavedCards = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const { data } = await api.get<{ cards: SavedCard[] }>(
        '/memories/payment-cards',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSavedCards(data.cards || []);
    } catch (err) {
      console.error('Error fetching payment cards:', err);
    }
  }, [getAccessTokenSilently]);

  // ── Check spend readiness on open ──────────────────────────────────
  const checkReadiness = useCallback(async () => {
    // For purchases, skip check if no amount (nothing to pay for)
    if (!isDeposit && (!totalAmount || totalAmount <= 0)) {
      setPhase('ready');
      return;
    }

    setPhase('checking');
    setErrorMessage(null);

    try {
      const token = await getAccessTokenSilently();

      if (isDeposit) {
        // Deposit flow: only check that purchase credit balance is non-negative
        const { data } = await api.post<DepositReadiness>(
          '/credits/check-deposit-readiness',
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );

        setDepositReadiness(data);

        if (data.ready) {
          setPhase('ready');
        } else {
          // Negative purchase balance — need top-up to clear debt
          setTopUpOption(data.topUpOption || null);
          setPhase('topup-required');
        }
        return;
      }

      // Purchase flow: check purchase credit balance
      const { data } = await api.post<SpendReadiness>(
        '/credits/check-spend-readiness',
        { amount: totalAmount },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setReadiness(data);

      if (data.sufficient) {
        setPhase('ready');
      } else {
        setTopUpOption(data.topUpOption || null);
        setPhase('topup-required');
      }
    } catch (err) {
      console.error('Error checking spend readiness:', err);
      // On error, allow the user to proceed — the backend safety net
      // in the human-task creation flow will catch any actual shortfall.
      setPhase('ready');
    }
  }, [totalAmount, isDeposit, getAccessTokenSilently]);

  useEffect(() => {
    if (show && needsLogin) {
      // Logged out: skip the auth-requiring readiness/cards/onboarding work; a
      // redirect to login is in flight.
      return;
    }
    if (show) {
      // Determine which onboarding key applies
      const onboardingKey = agentCardOnly
        ? 'spending_onboarding_agent_only'
        : 'spending_onboarding_both';

      if (!onboardingLoading && !hasSeenModal(onboardingKey)) {
        setShowOnboarding(true);
      } else {
        setShowOnboarding(false);
      }

      checkReadiness();
      fetchSavedCards();
      setSelectedCard(isDeposit ? '' : 'agent');
      setActiveSection('savedCards');
      setCardHint('');
    } else {
      setPhase('checking');
      setReadiness(null);
      setDepositReadiness(null);
      setTopUpOption(null);
      setErrorMessage(null);
      setSavedCards([]);
      setShowOnboarding(false);
      setActiveSection('savedCards');
      setCardHint('');
    }
  }, [
    show,
    checkReadiness,
    fetchSavedCards,
    isDeposit,
    agentCardOnly,
    onboardingLoading,
    hasSeenModal,
  ]);

  // ── Top up & approve (single action) ─────────────────────────────
  const handleTopupAndApprove = useCallback(async () => {
    if (!topUpOption) return;

    setPhase('topping-up');
    setErrorMessage(null);

    try {
      const token = await getAccessTokenSilently();
      const { data } = await api.post(
        '/credits/topup-balance',
        { priceId: topUpOption.priceId, quantity: topUpOption.quantity },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (data.success) {
        // Balance is now sufficient — proceed to approve immediately
        onApprove({ cardType: 'agent' });
      } else {
        setPhase('topup-required');
        setErrorMessage('Top-up failed. Please try again.');
      }
    } catch (err) {
      console.error('Error topping up balance:', err);
      setPhase('topup-required');
      setErrorMessage('Top-up failed. Please try again.');
    }
  }, [topUpOption, getAccessTokenSilently, onApprove]);

  const handleAddCardSuccess = useCallback((card: AddPaymentCardResult) => {
    setSavedCards((prev) => [
      {
        id: card.credentialId,
        label: card.label,
        cardLastFour: card.cardLastFour,
        cardNetwork: card.cardNetwork,
        createdAt: new Date().toISOString(),
      },
      ...prev,
    ]);
    setSelectedCard(card.credentialId);
    setShowAddCardModal(false);
  }, []);

  const currency = totalEntries[0]?.[0] || 'GBP';

  // ── Resolve the action button label & handler ──────────────────────
  const getActionButton = () => {
    // If user card is selected, bypass balance checks entirely
    if (isUserCardSelected) {
      return {
        label: isDeposit ? 'Approve reservation' : 'Approve spend',
        onClick: () =>
          onApprove({ cardType: 'user_card', credentialId: selectedCard }),
        disabled: false,
      };
    }

    // Merchant-stored card: no balance check needed
    if (isMerchantStoredSelected) {
      return {
        label: 'Approve spend',
        onClick: () =>
          onApprove({
            cardType: 'merchant_stored',
            ...(cardHint.trim() ? { cardHint: cardHint.trim() } : {}),
          }),
        disabled: false,
      };
    }

    switch (phase) {
      case 'checking':
      case 'topping-up':
        return {
          label: phase === 'checking' ? 'Checking balance...' : 'Processing...',
          onClick: undefined as (() => void) | undefined,
          disabled: true,
        };
      case 'ready':
        return {
          label: isDeposit ? 'Approve reservation' : 'Approve spend',
          onClick: () => onApprove({ cardType: 'agent' }),
          disabled: false,
        };
      case 'topup-required':
        if (topUpOption) {
          return {
            label: isDeposit
              ? `Top-up ${formatPrice(topUpOption.totalCredits, topUpOption.currency)} and reserve`
              : `Top-up ${formatPrice(topUpOption.totalCredits, topUpOption.currency)} and approve`,
            onClick: handleTopupAndApprove,
            disabled: false,
          };
        }
        return {
          label: 'Go to Billing',
          onClick: () => window.open('/settings/billing', '_blank'),
          disabled: false,
        };
      case 'error':
        return {
          label: 'Try anyway',
          onClick: () => onApprove({ cardType: 'agent' }),
          disabled: false,
        };
    }
  };

  const action = getActionButton();

  const spendContent = (
    <>
      <div className='spend-sheet-header'>
        <h2>{isDeposit ? 'Confirm deposit' : 'Confirm spend'}</h2>
        <button
          className='spend-sheet-close-btn'
          onClick={onCancel}
          aria-label='Close'
        >
          <CloseIcon />
        </button>
      </div>

      <div className='spend-sheet-body'>
        {isDeposit ? (
          <p className='spend-sheet-description'>
            I'm going to make a reservation on your behalf.
          </p>
        ) : (
          <p className='spend-sheet-description'>
            I'm going to make a purchase on your behalf.
          </p>
        )}

        {/* Item field — moved up */}
        {bookableItems.length > 0 && (
          <div className='spend-sheet-field'>
            <label>
              {isDeposit
                ? 'Reserving'
                : bookableItems.length === 1
                  ? 'Item'
                  : 'Items'}
            </label>
            <div className='spend-sheet-value'>
              {bookableItems.map((item, index) => (
                <React.Fragment key={item.id || index}>
                  {index > 0 && <br />}
                  {item.descriptionLine}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* I won't spend more than */}
        {!isDeposit && totalDisplay && (
          <div className='spend-sheet-field'>
            <label>I won't spend more than</label>
            <div className='spend-sheet-card-value'>{totalDisplay}</div>
          </div>
        )}

        {/* Choose card section — accordion behaviour */}
        <div className='spend-sheet-card-chooser'>
          {activeSection === 'savedCards' ? (
            <>
              <label className='spend-sheet-chooser-label'>
                Your saved cards
              </label>

              {/* Agent card — only for purchase flow */}
              {!isDeposit && (
                <button
                  className={`card-choice ${isAgentCardSelected ? 'card-choice--selected' : ''} card-network-agent`}
                  onClick={() => setSelectedCard('agent')}
                  type='button'
                >
                  <div className='card-left'>
                    <CardAgentIcon />
                    <span className='card-nickname'>{cardLabel}</span>
                  </div>
                  <div className='card-right'>
                    {readiness ? (
                      <div className='card-right-column'>
                        <span className='card-balance-label'>Balance</span>
                        <span className='card-balance-value'>
                          {formatPrice(readiness.balance, currency)}
                        </span>
                      </div>
                    ) : phase === 'checking' ? (
                      <span className='card-balance-loading'>…</span>
                    ) : null}
                  </div>
                </button>
              )}

              {/* Saved user cards — hidden for in-person purchase actions */}
              {!agentCardOnly &&
                savedCards.map((card) => (
                  <button
                    key={card.id}
                    className={`card-choice ${selectedCard === card.id ? 'card-choice--selected' : ''} card-network-${card.cardNetwork || 'other'}`}
                    onClick={() => setSelectedCard(card.id)}
                    type='button'
                  >
                    <div className='card-left'>
                      {getCardIcon(card.cardNetwork)}
                      <span className='card-nickname'>{card.label}</span>
                    </div>
                    <div className='card-right'>
                      <span className='card-last-four'>
                        •••• {card.cardLastFour || '????'}
                      </span>
                    </div>
                  </button>
                ))}

              {/* Add a payment card — hidden for in-person purchase actions */}
              {!agentCardOnly && (
                <button
                  className='card-choice card-choice--add'
                  onClick={() => setShowAddCardModal(true)}
                  type='button'
                >
                  <div className='card-left'>
                    <CardAddIcon />
                    <span className='card-nickname'>Add a payment card</span>
                  </div>
                </button>
              )}

              {/* Merchant-stored option — only if merchantName is known, not deposit, not agent-only */}
              {!isDeposit && !agentCardOnly && merchantName && (
                <>
                  <span className='spend-sheet-or-separator'>OR</span>
                  <button
                    className='card-choice card-choice--add'
                    onClick={() => {
                      setActiveSection('merchantStored');
                      setSelectedCard('');
                    }}
                    type='button'
                  >
                    <div className='card-left'>
                      <CardOtherIcon />
                      <span className='card-nickname'>
                        Use a payment method saved on {merchantName}
                      </span>
                    </div>
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              {/* Collapsed saved-cards button */}
              <button
                className='card-choice card-choice--add'
                onClick={() => {
                  setActiveSection('savedCards');
                  setSelectedCard(isDeposit ? '' : 'agent');
                  setCardHint('');
                }}
                type='button'
              >
                <div className='card-left'>
                  <CardOtherIcon />
                  <span className='card-nickname'>Use your saved cards</span>
                </div>
              </button>

              {/* Merchant-stored card hint textarea */}
              <div className='spend-sheet-merchant-hint-section'>
                <label className='spend-sheet-merchant-hint-label'>
                  Describe your payment method on {merchantName}
                </label>
                <textarea
                  className='spend-sheet-card-hint-textarea'
                  placeholder='Eg. Use my AMEX ending 1701'
                  value={cardHint}
                  onChange={(e) => setCardHint(e.target.value)}
                  maxLength={500}
                  rows={3}
                />
              </div>
            </>
          )}

          {agentCardOnly && (
            <p className='spend-sheet-agent-only-note'>
              This purchase will be made in person using {cardLabel}.
            </p>
          )}
        </div>

        {/* Shortfall message — only when agent card is selected and balance insufficient */}
        {!isDeposit &&
          isAgentCardSelected &&
          phase === 'topup-required' &&
          readiness && (
            <p className='spend-sheet-shortfall'>
              You need another{' '}
              <strong className='spend-sheet-shortfall-amount'>
                {formatPrice(Math.ceil(readiness.shortfall), currency)}
              </strong>
              {' to complete this spend. Continue below to top-up.'}
            </p>
          )}

        {/* Deposit flow: negative balance banner */}
        {isDeposit && phase === 'topup-required' && depositReadiness && (
          <div className='spend-sheet-negative-banner'>
            <span className='spend-sheet-negative-banner-bold'>
              {cardLabel} currently has a negative balance.
            </span>
            <br />
            To make this reservation you need to{' '}
            <span className='spend-sheet-negative-banner-amount'>
              add{' '}
              {formatPrice(
                Math.ceil(depositReadiness.shortfall || 0),
                currency,
              )}
            </span>
            .
            <br />
            Continue below to top up.
          </div>
        )}

        {errorMessage && (
          <div className='spend-sheet-error'>{errorMessage}</div>
        )}

        {!isDeposit && bookableItems.some((item) => item.purchaseLink) && (
          <div className='spend-sheet-field'>
            <label>Link</label>
            <div className='spend-sheet-value'>
              {bookableItems
                .filter((item) => item.purchaseLink)
                .map((item, index) => (
                  <React.Fragment key={item.id || index}>
                    {index > 0 && <br />}
                    <a
                      href={item.purchaseLink}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {item.purchaseLink}
                    </a>
                  </React.Fragment>
                ))}
            </div>
          </div>
        )}

        {!isDeposit && bookableItems.some((item) => item.delivery) && (
          <div className='spend-sheet-field'>
            <label>Delivery</label>
            <div className='spend-sheet-value'>
              {bookableItems
                .filter((item) => item.delivery)
                .map((item, index) => (
                  <React.Fragment key={item.id || index}>
                    {index > 0 && <br />}
                    {item.delivery}
                  </React.Fragment>
                ))}
            </div>
          </div>
        )}

        {!isDeposit && bookableItems.some((item) => item.deliveryAddress) && (
          <div className='spend-sheet-field'>
            <label>Deliver to</label>
            <div className='spend-sheet-value'>
              {bookableItems
                .filter((item) => item.deliveryAddress)
                .map((item, index) => (
                  <React.Fragment key={item.id || index}>
                    {index > 0 && <br />}
                    {item.deliveryAddress}
                  </React.Fragment>
                ))}
            </div>
          </div>
        )}

        {context && (
          <div className='spend-sheet-field'>
            <label>Context</label>
            <div className='spend-sheet-value'>{context}</div>
          </div>
        )}
      </div>

      <div className='spend-sheet-footer'>
        {onReject ? (
          <button className='spend-sheet-reject-btn' onClick={onReject}>
            Reject
          </button>
        ) : (
          <button className='spend-sheet-cancel-btn' onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          className='spend-sheet-approve-btn'
          onClick={action.onClick}
          disabled={action.disabled}
        >
          {action.label}
        </button>
      </div>
    </>
  );

  // Logged out: a redirect to login is in flight (see effect above). Render
  // nothing rather than the onboarding/spend sheet, which would otherwise loop.
  if (needsLogin) return null;

  if (inline) {
    if (!show) return null;
    return (
      <>
        {showOnboarding && (
          <SpendingOnboardingModal
            show
            onNext={() => setShowOnboarding(false)}
            agentCardOnly={agentCardOnly}
            agentImageUrl={agentImageUrl}
          />
        )}
        {!showOnboarding && (
          <>
            <div className='spend-sheet-shroud' />
            <div className='spend-sheet-inline'>{spendContent}</div>
          </>
        )}
        <AddPaymentCardModal
          show={showAddCardModal}
          onClose={() => setShowAddCardModal(false)}
          onSuccess={handleAddCardSuccess}
        />
      </>
    );
  }

  return (
    <>
      <SpendingOnboardingModal
        show={show && showOnboarding}
        onNext={() => setShowOnboarding(false)}
        agentCardOnly={agentCardOnly}
        agentImageUrl={agentImageUrl}
      />
      <Modal
        show={show && !showAddCardModal && !showOnboarding}
        onHide={onCancel}
        centered
        dialogClassName='response-artifact-modal spending-approval-modal'
        backdropClassName='spending-approval-backdrop'
      >
        {agentImageUrl && (
          <div className='spend-sheet-agent-avatar'>
            <img src={agentImageUrl} alt='' />
          </div>
        )}
        <Modal.Body>
          <div className='spend-sheet-modal'>{spendContent}</div>
        </Modal.Body>
      </Modal>
      <AddPaymentCardModal
        show={showAddCardModal}
        onClose={() => setShowAddCardModal(false)}
        onSuccess={handleAddCardSuccess}
      />
    </>
  );
};

export default SpendingApprovalModal;
